import React, { useState } from "react";
import { useAppDispatch } from "../../../../../redux/hooks";
import ActionMenu, {
  ActionMenuItem,
} from "../../../../../components/actionMenu/ActionMenu";
import {
  getActivateDeliveryArrangement,
  getDeliveryArrangement,
  postCancelAwb,
  postChangeClient,
} from "../../../../../redux/api/picking.api";
import { getSyncronizeInvoices } from "../../../../../redux/api/external.api";
import { pickingRoutes } from "../../../../../router/routesConstants";
import {
  checkEndpointStatus,
  endpointStatus,
} from "../../../../../helpers/apiHelper";
import usePermissions from "../../../../../router/usePermissions";
import userRoles from "../../../../../constants/userRoles";
import { dateForDatabase } from "../../../../../utils/dateAndTime";
import {
  openChangeDateDeliveryArrangementModal,
  setSelectedPackageChangingDate,
} from "../../../../../redux/reducer/picking.reducer";

function DeliveryArrangementActionButton(props: any) {
  const dispatch = useAppDispatch();
  const { isAuthorized } = usePermissions();

  const [isLoading, setIsLoading] = useState(false);

  const formatDate = dateForDatabase(props.tableContext.tableSearch?.createdAt);

  const handleActivateDeliveryArrangement = async () => {
    setIsLoading(true);

    try {
      await dispatch(
        getActivateDeliveryArrangement({
          params: {
            op: props.crudData.Op,
            page: props.tableContext.tablePagination.page,
            perPage: props.tableContext.tablePagination.rowsPerPage,
            status: props.tableContext.tableSearch?.status ?? 0,
            facturi: props.tableContext.tableSearch?.facturi,
            ruta: props.tableContext.tableSearch?.ruta,
            deliveryPoint: props.tableContext.tableSearch?.deliveryPoint,
          },
          callBack: () => {
            dispatch(
              getDeliveryArrangement({
                page: props.tableContext.tablePagination.page,
                perPage: props.tableContext.tablePagination.rowsPerPage,
                status: props.tableContext.tableSearch?.status ?? 0,
                facturi: props.tableContext.tableSearch?.facturi ?? "",
                ruta: props.tableContext.tableSearch?.ruta ?? "",
                deliveryPoint:
                  props.tableContext.tableSearch?.deliveryPoint ?? "",
                commandNumber:
                  props.tableContext.tableSearch?.commandNumber ?? "",
                createdAt: formatDate,
              }),
            );
          },
        }),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSyncronizeInvoices = () => {
    dispatch(
      getSyncronizeInvoices({
        op: props.crudData.Op,
      }),
    );
  };

  const handleChangeDeliveryPoint = async () => {
    if (!props.crudData.Op) {
      return;
    }

    setIsLoading(true);

    const resultAction = await dispatch(
      postChangeClient({
        op: props.crudData.Op,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postChangeClient) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postChangeClient) ===
      endpointStatus.fulfilled
    ) {
      setIsLoading(false);

      dispatch(
        getDeliveryArrangement({
          page: props.tableContext.tablePagination.page,
          perPage: props.tableContext.tablePagination.rowsPerPage,
          status: props.tableContext.tableSearch?.status ?? 0,
          facturi: props.tableContext.tableSearch?.facturi ?? "",
          ruta: props.tableContext.tableSearch?.ruta ?? "",
          deliveryPoint: props.tableContext.tableSearch?.deliveryPoint ?? "",
          commandNumber: props.tableContext.tableSearch?.commandNumber ?? "",
          createdAt: formatDate,
        }),
      );
    }
  };

  const handleCancelAwb = async () => {
    setIsLoading(true);

    const resultAction = await dispatch(
      postCancelAwb({
        doc_trans: props.crudData.doc_trans,
      }),
    );

    if (
      checkEndpointStatus(resultAction, postCancelAwb) !==
      endpointStatus.pending
    ) {
      setIsLoading(false);
    }

    if (
      checkEndpointStatus(resultAction, postCancelAwb) ===
      endpointStatus.fulfilled
    ) {
      dispatch(
        getDeliveryArrangement({
          page: props.tableContext.tablePagination.page,
          perPage: props.tableContext.tablePagination.rowsPerPage,
          status: props.tableContext.tableSearch?.status ?? 0,
          facturi: props.tableContext.tableSearch?.facturi ?? "",
          ruta: props.tableContext.tableSearch?.ruta ?? "",
          deliveryPoint: props.tableContext.tableSearch?.deliveryPoint ?? "",
          commandNumber: props.tableContext.tableSearch?.commandNumber ?? "",
          createdAt: formatDate,
        }),
      );
    }
  };

  const handleChangePickingDate = () => {
    dispatch(setSelectedPackageChangingDate(props.crudData));
    dispatch(openChangeDateDeliveryArrangementModal());
  };

  const items: ActionMenuItem[] = [
    {
      id: "toggle-command",
      label:
        props.crudData.status === 0
          ? "Activeaza comanda"
          : "Dezactiveaza comanda",
      icon: "ACTIVATE",
      tooltip: !(props.crudData.status <= 1)
        ? "Comanda nu mai poate fi activata sau dezactivata in starea curenta."
        : props.crudData.status === 0
        ? "Activeaza aceasta comanda dupa confirmare."
        : "Dezactiveaza aceasta comanda dupa confirmare.",
      disabled: !(props.crudData.status <= 1),
      loading: isLoading,
      color: props.crudData.status === 0 ? "success" : "warning",
      withConfirmationModal: {
        modalLeftButtonVariant: "outlined",
        modalTitle: `Doriti sa ${
          props.crudData.status === 0 ? "activati" : "dezactivati"
        } comanda nr: ${props.crudData.id}?`,
        modalRightButtonOnClick: handleActivateDeliveryArrangement,
      },
    },
    {
      id: "resync-invoices",
      dividerBefore: true,
      label: "Resincronizeaza facturile",
      icon: "RESYNC",
      tooltip:
        "Reincarca din ERP facturile si pozitiile de produse ale comenzii, dupa confirmare.",
      loading: isLoading,
      withConfirmationModal: {
        modalLeftButtonVariant: "outlined",
        modalTitle: `Doriti sa resincronizati facturile din comanda nr: ${props.crudData.id}?`,
        modalRightButtonOnClick: handleSyncronizeInvoices,
      },
    },
    {
      id: "split-invoices",
      label: "Imparte facturile",
      icon: "SPLIT_LIST",
      tooltip: "Deschide impartirea facturilor pentru aceasta comanda.",
      loading: isLoading,
      navigate: `/${pickingRoutes.picking}/${pickingRoutes.splitInvoices}?delivery=${props.crudData.pctDeLivr}&op=${props.crudData.Op}&status=${props.crudData.status}`,
    },
    {
      id: "change-delivery-point",
      label: "Schimba punctul de livrare",
      icon: "CHANGE_ROUTE",
      tooltip:
        "Actualizeaza punctul de livrare dupa datele facturilor; facturile modificate pot fi separate intr-o comanda noua.",
      hidden: !isAuthorized(userRoles.CHANGE_DELIVERY_POINT),
      loading: isLoading,
      withConfirmationModal: {
        modalLeftButtonVariant: "outlined",
        modalTitle: `Doriti sa schimbati punctul de livrare al clientului pentru comanda nr: ${props.crudData.Op}?`,
        modalRightButtonOnClick: handleChangeDeliveryPoint,
      },
    },
    {
      id: "change-pickup-date",
      dividerBefore: true,
      label: "Schimba data ridicarii",
      icon: "CHANGE_DATE",
      tooltip: !props.crudData.doc_trans
        ? "Comanda nu are un AWB pentru modificarea datei de ridicare."
        : "Deschide alegerea datei si orei de ridicare pentru AWB-ul comenzii.",
      disabled: !props.crudData.doc_trans,
      onClick: handleChangePickingDate,
    },
    {
      id: "cancel-awb",
      label: "Anuleaza AWB",
      icon: "CANCEL",
      tooltip: !props.crudData.doc_trans
        ? "Comanda nu are un AWB de anulat."
        : "Solicita anularea AWB-ului in TMS dupa confirmare.",
      disabled: !props.crudData.doc_trans,
      loading: isLoading,
      color: "error",
      withConfirmationModal: {
        modalTitle: "Anuleaza AWB",
        modalSubtitle: `Doriti sa anulati awb-ul ${props.crudData.doc_trans}?`,
        modalLeftButtonVariant: "outlined",
        modalRightButtonOnClick: handleCancelAwb,
      },
    },
  ];

  return (
    <ActionMenu
      items={items}
      minItemsForMenu={2}
      title={`Comanda ${props.crudData.id}`}
      ariaLabel={`Actiuni pentru comanda ${props.crudData.id}`}
    />
  );
}

export default DeliveryArrangementActionButton;
